import React, { useState } from 'react';
import {
  AlertTriangle,
  Bell,
  Calendar,
  CheckCircle2,
  ChevronDown,
  Mail,
  Phone,
  Plus,
  Trash2,
  X,
  Zap,
} from 'lucide-react';

export const BUILTIN_CADENCES = [
  {
    id: 'cadence-warm-lead',
    name: 'Warm Lead Nurture',
    description: 'For freshly captured inbound leads who need a structured intro sequence.',
    steps: [
      { day: 0,  channel: 'email', label: 'Intro / value pitch' },
      { day: 3,  channel: 'call',  label: 'Discovery call' },
      { day: 7,  channel: 'email', label: 'Case study or relevant placement share' },
      { day: 14, channel: 'email', label: 'Check-in follow-up' },
      { day: 21, channel: 'call',  label: 'Proposal / next steps discussion' },
    ],
  },
  {
    id: 'cadence-re-engage',
    name: 'Re-engagement — 30+ day cold',
    description: 'Re-activate accounts that have gone quiet for 30+ days.',
    steps: [
      { day: 0,  channel: 'email', label: 'We miss you — market update' },
      { day: 5,  channel: 'call',  label: 'Quick catch-up call' },
      { day: 12, channel: 'email', label: 'Relevant opportunity or talent share' },
      { day: 20, channel: 'call',  label: 'Final check-in' },
    ],
  },
  {
    id: 'cadence-post-placement',
    name: 'Post-Placement Client Check-in',
    description: 'Keep clients happy after a successful hire. Drives repeat business.',
    steps: [
      { day: 7,  channel: 'call',  label: '1-week check-in call' },
      { day: 30, channel: 'email', label: '30-day satisfaction email' },
      { day: 90, channel: 'call',  label: 'Quarterly business review call' },
    ],
  },
  {
    id: 'cadence-new-prospect',
    name: 'New Prospect — Cold Outreach',
    description: 'For contacts captured via LinkedIn or website who haven\'t engaged yet.',
    steps: [
      { day: 0,  channel: 'email', label: 'Cold intro — who we are' },
      { day: 4,  channel: 'email', label: 'Value add — talent market insight' },
      { day: 9,  channel: 'call',  label: 'Intro call attempt' },
      { day: 14, channel: 'email', label: 'Breakup email — last touch' },
    ],
  },
];

export const AUTOMATION_TRIGGERS = [
  // ── Account Health ──────────────────────────────────────────────────────
  { id: 'no_touch_days',               group: 'Account Health',      label: 'Account not touched for N days',                 valueType: 'number',  valuePlaceholder: '30',   valueLabel: 'Days',   hint: 'Fires when no email, call or meeting has been logged against this account.' },
  { id: 'contact_no_touch_days',       group: 'Account Health',      label: 'Contact not touched for N days',                 valueType: 'number',  valuePlaceholder: '14',   valueLabel: 'Days',   hint: 'Individual contact has had no activity logged.' },
  { id: 'risk_level_is',               group: 'Account Health',      label: 'Account risk level becomes',                     valueType: 'select',  options: ['Critical', 'At risk', 'Needs attention', 'Watch'],  hint: 'Triggered when the computed risk score changes to this level.' },
  { id: 'sentiment_drops',             group: 'Account Health',      label: 'Account sentiment drops to',                     valueType: 'select',  options: ['Cold', 'Negative', 'Neutral'],  hint: 'Based on most recent interaction log sentiment.' },
  { id: 'no_activity_this_week',       group: 'Account Health',      label: 'No CRM activity logged in N days',               valueType: 'number',  valuePlaceholder: '7',    valueLabel: 'Days',   hint: 'Catch-all for quiet periods — surface stalled accounts.' },
  { id: 'account_created_days_ago',    group: 'Account Health',      label: 'New account created N days ago (no touchpoint)', valueType: 'number',  valuePlaceholder: '3',    valueLabel: 'Days',   hint: 'Nudge to make first contact after a new account is created.' },
  { id: 'account_first_touchpoint',   group: 'Account Health',      label: 'First ever touchpoint just logged',              valueType: null,      hint: 'Triggers on the very first email, call or meeting logged to help start a cadence.' },
  { id: 'long_term_client_anniversary', group: 'Account Health',    label: 'Client relationship anniversary (N years)',      valueType: 'number',  valuePlaceholder: '1',    valueLabel: 'Years',  hint: 'Great for sending a personal thank-you.' },
  { id: 'nps_score_low',               group: 'Account Health',      label: 'NPS / satisfaction score is low',                valueType: null,      hint: 'When a satisfaction flag is set on a contact or account.' },

  // ── Pipeline & Deals ────────────────────────────────────────────────────
  { id: 'opportunity_stage',           group: 'Pipeline & Deals',    label: 'Opportunity moves to stage',                     valueType: 'select',  options: ['Lead', 'Opportunity', 'Proposal', 'Closed-Won', 'Closed-Lost'],  hint: 'Classic stage-change trigger for automated follow-ups.' },
  { id: 'deal_stuck_days',             group: 'Pipeline & Deals',    label: 'Opportunity stuck in same stage for N days',     valueType: 'number',  valuePlaceholder: '14',   valueLabel: 'Days',   hint: 'Flag stalled deals that need a nudge or a loss review.' },
  { id: 'deal_closed_won',             group: 'Pipeline & Deals',    label: 'Opportunity closed — Won',                       valueType: null,      hint: 'Celebrate, onboard and ask for a referral.' },
  { id: 'deal_closed_lost',            group: 'Pipeline & Deals',    label: 'Opportunity closed — Lost',                     valueType: null,      hint: 'Trigger a re-engage cadence or post-mortem task.' },
  { id: 'proposal_no_reply_days',      group: 'Pipeline & Deals',    label: 'Proposal sent — no reply for N days',            valueType: 'number',  valuePlaceholder: '5',    valueLabel: 'Days',   hint: 'Follow up on outstanding proposals automatically.' },
  { id: 'high_value_deal_opened',      group: 'Pipeline & Deals',    label: 'New deal value ≥ N (high-value alert)',          valueType: 'number',  valuePlaceholder: '10000',valueLabel: '£ / $',  hint: 'Escalate high-value opportunities for personal attention.' },
  { id: 'multiple_open_deals',         group: 'Pipeline & Deals',    label: 'Account has 3+ open opportunities at once',      valueType: null,      hint: 'Signals a hot account — make sure top-tier attention is applied.' },
  { id: 'contract_expiring_days',      group: 'Pipeline & Deals',    label: 'Retainer / contract expiring in N days',         valueType: 'number',  valuePlaceholder: '30',   valueLabel: 'Days',   hint: 'Prompt a renewal conversation before the contract lapses.' },
  { id: 'budget_renewal_season',       group: 'Pipeline & Deals',    label: 'Budget renewal season (month)',                  valueType: 'select',  options: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],  hint: 'Get ahead of year-start or fiscal-start spend windows.' },

  // ── Placement & Candidates ──────────────────────────────────────────────
  { id: 'days_since_hire',             group: 'Placement & Candidates', label: 'Days since last placement with this account', valueType: 'number',  valuePlaceholder: '90',   valueLabel: 'Days',   hint: 'Re-open the conversation if a client has gone quiet since the last fill.' },
  { id: 'candidate_placed',            group: 'Placement & Candidates', label: 'Candidate successfully placed',              valueType: null,      hint: 'Auto-create an onboarding check-in task and schedule a post-placement call.' },
  { id: 'first_placement_made',        group: 'Placement & Candidates', label: 'First-ever placement with this client',      valueType: null,      hint: 'Kick off a loyalty nurture cadence after the landmark first placement.' },
  { id: 'placement_anniversary_months', group: 'Placement & Candidates', label: 'Placement anniversary (every N months)',   valueType: 'number',  valuePlaceholder: '6',    valueLabel: 'Months', hint: 'Regular check-ins keep placed candidates warm for extensions or perm conversion.' },
  { id: 'start_date_approaching',      group: 'Placement & Candidates', label: 'Candidate start date within N days',         valueType: 'number',  valuePlaceholder: '7',    valueLabel: 'Days',   hint: 'Final prep call, paperwork reminder and client briefing.' },
  { id: 'contract_end_approaching',    group: 'Placement & Candidates', label: 'Contractor end date within N days',          valueType: 'number',  valuePlaceholder: '30',   valueLabel: 'Days',   hint: 'Initiate extension conversation or surface a replacement candidate.' },
  { id: 'probation_ending',            group: 'Placement & Candidates', label: 'Probation period ending in N days',          valueType: 'number',  valuePlaceholder: '14',   valueLabel: 'Days',   hint: 'Check in with client and placed candidate ahead of probation sign-off.' },
  { id: 'counter_offer_risk',          group: 'Placement & Candidates', label: 'Counter-offer risk flagged on candidate',    valueType: null,      hint: 'Trigger urgent task to reinforce candidate commitment.' },
  { id: 'candidate_no_contact_days',   group: 'Placement & Candidates', label: 'Placed candidate not contacted for N days',  valueType: 'number',  valuePlaceholder: '21',   valueLabel: 'Days',   hint: 'Keep your placed talent warm for future roles.' },

  // ── Email Engagement ─────────────────────────────────────────────────────
  { id: 'email_opened',                group: 'Email Engagement',    label: 'Cadence email opened',                           valueType: null,      hint: 'Contact opened your email — strike while iron is hot with a follow-up call task.' },
  { id: 'email_clicked',               group: 'Email Engagement',    label: 'Cadence email link clicked',                     valueType: null,      hint: 'High buying-intent signal — escalate to immediate outreach.' },
  { id: 'email_replied',               group: 'Email Engagement',    label: 'Cadence email replied to',                       valueType: null,      hint: 'Auto-advance cadence or create a meeting booking task.' },
  { id: 'no_open_after_n_emails',      group: 'Email Engagement',    label: 'No email opens after N sends',                   valueType: 'number',  valuePlaceholder: '3',    valueLabel: 'Emails', hint: "Switch channel \u2014 create a call task if email isn't working." },
  { id: 'email_bounced',               group: 'Email Engagement',    label: 'Email hard-bounced',                             valueType: null,      hint: 'Flag contact for email update and try phone or LinkedIn.' },
  { id: 'cadence_completed',           group: 'Email Engagement',    label: 'Contact completes all cadence steps',            valueType: null,      hint: 'Archive or enroll in a maintenance drip.' },
  { id: 'cadence_step_overdue_days',   group: 'Email Engagement',    label: 'Cadence step overdue by N days',                 valueType: 'number',  valuePlaceholder: '3',    valueLabel: 'Days',   hint: 'Remind recruiter to execute the overdue step.' },

  // ── Contact Lifecycle ────────────────────────────────────────────────────
  { id: 'new_contact_added',           group: 'Contact Lifecycle',   label: 'New contact added to account',                   valueType: null,      hint: 'Welcome sequence or intro call task for every new stakeholder.' },
  { id: 'contact_stage_change',        group: 'Contact Lifecycle',   label: 'Contact moves to relationship stage',            valueType: 'select',  options: ['Prospect', 'Nurture', 'Active', 'Key Account', 'Dormant'],  hint: 'Trigger stage-appropriate actions automatically.' },
  { id: 'contact_role_change',         group: 'Contact Lifecycle',   label: 'Contact changes job title / company',            valueType: null,      hint: 'Congratulate on promotion or reconnect at new employer.' },
  { id: 'referral_received',           group: 'Contact Lifecycle',   label: 'Referral received from this contact',            valueType: null,      hint: 'Send a thank-you and deepen the relationship.' },
  { id: 'vip_tag_applied',             group: 'Contact Lifecycle',   label: 'VIP or key account tag applied',                 valueType: null,      hint: 'Assign a premium cadence and set a high-priority review task.' },
  { id: 'contact_birthday',            group: 'Contact Lifecycle',   label: 'Contact birthday (within N days)',               valueType: 'number',  valuePlaceholder: '3',    valueLabel: 'Days',   hint: 'Personal touch that stands out from the competition.' },
  { id: 'contact_work_anniversary',    group: 'Contact Lifecycle',   label: 'Contact work anniversary at company',            valueType: null,      hint: 'Reconnect around milestone moments.' },

  // ── Task & Activity ──────────────────────────────────────────────────────
  { id: 'task_overdue_days',           group: 'Task & Activity',     label: 'CRM task overdue by N days',                     valueType: 'number',  valuePlaceholder: '2',    valueLabel: 'Days',   hint: 'Escalate priority and create a reminder.' },
  { id: 'high_priority_task_complete', group: 'Task & Activity',     label: 'High-priority task completed',                   valueType: null,      hint: 'Automatically create the logical next step.' },
  { id: 'call_logged_no_followup',     group: 'Task & Activity',     label: 'Call logged — no follow-up task created',        valueType: null,      hint: 'Ensure every call results in a next action.' },
  { id: 'meeting_logged',              group: 'Task & Activity',     label: 'Meeting just logged',                            valueType: null,      hint: 'Create a post-meeting recap and next-step task automatically.' },
  { id: 'no_task_open_for_account',    group: 'Task & Activity',     label: 'Account has no open tasks for N days',           valueType: 'number',  valuePlaceholder: '7',    valueLabel: 'Days',   hint: 'Keep every account in motion — no such thing as a truly quiet account.' },

  // ── Market & Intelligence ────────────────────────────────────────────────
  { id: 'job_opened_at_account',       group: 'Market & Intelligence', label: 'New role posted / opened at this account',    valueType: null,      hint: 'Prime moment to offer candidates and pitch your service.' },
  { id: 'hiring_freeze_lifted',        group: 'Market & Intelligence', label: 'Hiring freeze lifted (flag removed)',          valueType: null,      hint: 'Re-activate dormant accounts the moment they start hiring again.' },
  { id: 'competitor_activity',         group: 'Market & Intelligence', label: 'Competitor activity flagged on account',       valueType: null,      hint: 'Trigger a relationship-strengthening touch before you get displaced.' },
  { id: 'industry_news_flag',          group: 'Market & Intelligence', label: 'Account flagged as "in the news"',             valueType: null,      hint: 'Comment on news as a conversation opener.' },
  { id: 'funding_round',               group: 'Market & Intelligence', label: 'Account funding round flagged',                valueType: null,      hint: 'Funding usually means rapid headcount growth — strike early.' },
  { id: 'expansion_signal',            group: 'Market & Intelligence', label: 'Account expansion / new office flagged',       valueType: null,      hint: 'New locations = new hiring managers to meet.' },

  // ── Calendar & Time ──────────────────────────────────────────────────────
  { id: 'weekly_digest_trigger',       group: 'Calendar & Time',     label: 'Every Monday — weekly account check-in',         valueType: null,      hint: 'Start the week by surfacing accounts that need attention.' },
  { id: 'end_of_month',                group: 'Calendar & Time',     label: 'End of month — pipeline review',                 valueType: null,      hint: 'Prompt forecasting and close-of-month pushes.' },
  { id: 'quarter_start',               group: 'Calendar & Time',     label: 'Start of new quarter — QBR prep',                valueType: null,      hint: 'Prompt quarterly business review outreach to key accounts.' },
  { id: 'n_days_no_placement_qtr',     group: 'Calendar & Time',     label: 'No placement this quarter (N days in)',          valueType: 'number',  valuePlaceholder: '60',   valueLabel: 'Days',   hint: 'Alert when Q is running dry and push activity.' },
];

export const AUTOMATION_ACTIONS = [
  { id: 'create_task',     label: 'Create a CRM task',          params: ['title', 'priority'] },
  { id: 'enroll_cadence',  label: 'Enroll contact in cadence',  params: ['cadenceId'] },
  { id: 'send_cta',        label: 'Queue outreach CTA email',   params: [] },
];

const PRIORITY_OPTIONS = ['normal', 'high', 'urgent'];

const CHANNEL_ICON = { email: Mail, call: Phone, meeting: Calendar };

function CadenceStepDots({ steps, currentStep = -1 }) {
  return (
    <div className="flex items-center gap-1.5 mt-2">
      {steps.map((step, i) => {
        const Icon = CHANNEL_ICON[step.channel] || Zap;
        return (
          <React.Fragment key={i}>
            <div className={`flex h-5 w-5 items-center justify-center rounded-full border text-micro ${
              i < currentStep ? 'border-violet-400 bg-violet-400 text-slate-950' :
              i === currentStep ? 'border-violet-400 bg-violet-500/30 text-violet-300' :
              'border-white/10 bg-white/5 text-slate-600'
            }`} title={`Day ${step.day}: ${step.label}`}>
              <Icon size={8} />
            </div>
            {i < steps.length - 1 && <div className={`h-px flex-1 ${i < currentStep ? 'bg-violet-400/60' : 'bg-white/10'}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function CrmAutomationPanel({
  rules = [],
  cadenceEnrollments = [],
  pendingAutomations = [],
  emailTracking = [],       // crmEmailTracking records array
  smtpConfigured = false,
  onOpenSmtpSettings,       // () => void
  onSaveRule,           // (ruleData) => void
  onDeleteRule,         // (ruleId) => void
  onToggleRule,         // (ruleId, enabled) => void
  onDismissAutomation,  // (pendingId) => void
  onExecuteAutomation,  // (pending) => void — creates task / enrolls / sends CTA
  onClose,
}) {
  const [tab, setTab] = useState(pendingAutomations.length > 0 ? 'inbox' : 'rules');
  const [showBuilder, setShowBuilder] = useState(false);
  const [builderTrigger, setBuilderTrigger] = useState(AUTOMATION_TRIGGERS[0].id);
  const [builderTriggerVal, setBuilderTriggerVal] = useState('30');
  const [builderAction, setBuilderAction] = useState(AUTOMATION_ACTIONS[0].id);
  const [builderTaskTitle, setBuilderTaskTitle] = useState('Follow up with {company}');
  const [builderTaskPriority, setBuilderTaskPriority] = useState('normal');
  const [builderCadenceId, setBuilderCadenceId] = useState(BUILTIN_CADENCES[0].id);
  const [builderName, setBuilderName] = useState('');
  const [builderSaving, setBuilderSaving] = useState(false);

  const triggerDef = AUTOMATION_TRIGGERS.find((t) => t.id === builderTrigger);
  const actionDef = AUTOMATION_ACTIONS.find((a) => a.id === builderAction);

  const handleSave = async () => {
    if (!builderName.trim()) return;
    setBuilderSaving(true);
    const rule = {
      name: builderName.trim(),
      trigger: builderTrigger,
      triggerValue: triggerDef?.valueType === 'number' ? Number(builderTriggerVal) : builderTriggerVal,
      action: builderAction,
      actionParams: {
        ...(builderAction === 'create_task' ? { title: builderTaskTitle, priority: builderTaskPriority } : {}),
        ...(builderAction === 'enroll_cadence' ? { cadenceId: builderCadenceId } : {}),
      },
      enabled: true,
    };
    await onSaveRule?.(rule);
    setBuilderSaving(false);
    setShowBuilder(false);
    setBuilderName('');
  };

  const TABS = [
    { id: 'inbox',    label: `Inbox${pendingAutomations.length ? ` · ${pendingAutomations.length}` : ''}` },
    { id: 'rules',    label: `Rules (${rules.length})` },
    { id: 'cadences', label: 'Cadences' },
    { id: 'enrolled', label: `Enrolled (${cadenceEnrollments.filter(e => e.status === 'active').length})` },
    { id: 'analytics', label: 'Analytics' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-slate-950">
      {/* Top bar */}
      <div className="flex shrink-0 items-center justify-between border-b border-white/10 bg-slate-950/95 px-6 py-4 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <button type="button" onClick={onClose} className="flex items-center gap-2 rounded-ctl border border-white/10 bg-white/5 px-3 py-2 text-meta font-black uppercase tracking-[0.14em] text-slate-300 hover:text-white">
            <X size={13} /> Close
          </button>
          <div className="hidden text-xs text-slate-500 sm:block">CRM Automation &amp; Cadences</div>
        </div>
        <button
          type="button"
          onClick={() => { setShowBuilder(true); setTab('rules'); }}
          className="flex items-center gap-2 rounded-ctl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-meta font-black uppercase tracking-[0.14em] text-cyan-300 hover:bg-cyan-500/20 transition"
        >
          <Plus size={12} /> New rule
        </button>
      </div>

      {/* Tabs */}
      <div className="shrink-0 border-b border-white/10 px-8">
        <div className="mx-auto flex max-w-5xl overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`shrink-0 border-b-2 px-5 py-3.5 text-meta font-black uppercase tracking-[0.14em] transition ${
                tab === t.id ? 'border-cyan-400 text-cyan-300' : 'border-transparent text-slate-500 hover:text-slate-300'
              }`}
            >
              {t.label}
              {t.id === 'inbox' && pendingAutomations.length > 0 && (
                <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 py-0.5 text-micro font-black text-white">{pendingAutomations.length}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        <div className="mx-auto max-w-5xl">

          {/* ── INBOX — fired automation alerts ── */}
          {tab === 'inbox' && (
            <div className="max-w-2xl space-y-4">
              {pendingAutomations.length === 0 ? (
                <div className="rounded-card border border-dashed border-white/10 px-5 py-12 text-center">
                  <CheckCircle2 size={28} className="mx-auto mb-3 text-slate-700" />
                  <div className="text-sm font-bold text-slate-500">Automation inbox is clear.</div>
                  <div className="mt-1 text-xs text-slate-600">Triggered automations will appear here for review and one-click execution.</div>
                </div>
              ) : pendingAutomations.map((pending) => (
                <div key={pending.id} className="rounded-card border border-amber-400/20 bg-amber-500/5 p-5">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-start gap-3">
                      <Bell size={16} className="mt-0.5 shrink-0 text-amber-400" />
                      <div>
                        <div className="text-sm font-bold text-white">{pending.rule?.name}</div>
                        <div className="mt-0.5 text-xs text-slate-400">
                          Triggered for <span className="font-bold text-amber-300">{pending.client?.companyName || pending.contactName || 'Unknown'}</span>
                        </div>
                        <div className="mt-0.5 text-micro text-slate-500">
                          {pending.rule?.trigger === 'no_touch_days' && `No contact for ${pending.rule.triggerValue} days`}
                          {pending.rule?.trigger === 'risk_level_is' && `Risk level: ${pending.rule.triggerValue}`}
                          {pending.rule?.trigger === 'opportunity_stage' && `Opportunity reached ${pending.rule.triggerValue}`}
                        </div>
                      </div>
                    </div>
                    <div className="text-micro text-slate-600 shrink-0">{new Date(pending.firedAtIso || Date.now()).toLocaleDateString()}</div>
                  </div>
                  <div className="mb-3 rounded-ctl border border-white/[0.07] bg-white/[0.03] px-4 py-3 text-xs text-slate-300">
                    Action: <span className="font-bold text-white">
                      {pending.rule?.action === 'create_task' && `Create task — "${pending.rule.actionParams?.title?.replace('{company}', pending.client?.companyName || '')}"`}
                      {pending.rule?.action === 'enroll_cadence' && `Enroll in "${BUILTIN_CADENCES.find(c => c.id === pending.rule.actionParams?.cadenceId)?.name || 'cadence'}"`}
                      {pending.rule?.action === 'send_cta' && 'Queue outreach CTA email'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => onExecuteAutomation?.(pending)}
                      className="flex-1 rounded-full border border-cyan-400/30 bg-cyan-500/10 py-2 text-micro font-black text-cyan-300 hover:bg-cyan-500/20 transition"
                    >
                      Execute
                    </button>
                    <button
                      type="button"
                      onClick={() => onDismissAutomation?.(pending.id)}
                      className="rounded-full border border-white/10 bg-white/5 px-5 py-2 text-micro font-black text-slate-400 hover:text-white transition"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── RULES ── */}
          {tab === 'rules' && (
            <div className="max-w-3xl space-y-5">
              {/* Rule builder */}
              {showBuilder && (
                <div className="rounded-card border border-cyan-400/20 bg-cyan-500/5 p-5">
                  <div className="mb-4 text-meta font-black uppercase tracking-[0.2em] text-cyan-400">New automation rule</div>
                  <div className="space-y-4">
                    <div>
                      <label className="mb-1.5 block text-micro font-black uppercase tracking-[0.14em] text-slate-500">Rule name</label>
                      <input
                        value={builderName}
                        onChange={(e) => setBuilderName(e.target.value)}
                        placeholder="e.g. Re-engage cold accounts"
                        className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400"
                      />
                    </div>

                    {/* WHEN */}
                    <div className="rounded-ctl border border-white/[0.07] bg-white/[0.025] p-4">
                      <div className="mb-3 text-micro font-black uppercase tracking-[0.14em] text-slate-500">When</div>
                      <div className="flex flex-wrap items-start gap-3">
                        <div className="flex-1 min-w-[260px] space-y-2">
                          <select
                            value={builderTrigger}
                            onChange={(e) => { setBuilderTrigger(e.target.value); setBuilderTriggerVal(''); }}
                            className="w-full rounded-ctl border border-white/10 bg-slate-950 px-3 py-2 text-xs text-white outline-none focus:border-cyan-400"
                          >
                            {Object.entries(
                              AUTOMATION_TRIGGERS.reduce((acc, t) => {
                                (acc[t.group] = acc[t.group] || []).push(t);
                                return acc;
                              }, {})
                            ).map(([group, triggers]) => (
                              <optgroup key={group} label={`── ${group}`}>
                                {triggers.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                              </optgroup>
                            ))}
                          </select>
                          {triggerDef?.hint && (
                            <p className="text-micro text-slate-500 leading-4">{triggerDef.hint}</p>
                          )}
                        </div>
                        {triggerDef?.valueType === 'number' && (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={builderTriggerVal}
                              onChange={(e) => setBuilderTriggerVal(e.target.value)}
                              placeholder={triggerDef.valuePlaceholder || ''}
                              min={1}
                              className="w-24 rounded-ctl border border-white/10 bg-slate-950 px-3 py-2 text-xs text-white outline-none focus:border-cyan-400"
                            />
                            {triggerDef?.valueLabel && <span className="text-micro text-slate-500">{triggerDef.valueLabel}</span>}
                          </div>
                        )}
                        {triggerDef?.valueType === 'select' && (
                          <select
                            value={builderTriggerVal}
                            onChange={(e) => setBuilderTriggerVal(e.target.value)}
                            className="rounded-ctl border border-white/10 bg-slate-950 px-3 py-2 text-xs text-white outline-none focus:border-cyan-400"
                          >
                            <option value="">— choose —</option>
                            {(triggerDef.options || []).map((o) => <option key={o}>{o}</option>)}
                          </select>
                        )}
                      </div>
                    </div>

                    {/* THEN */}
                    <div className="rounded-ctl border border-white/[0.07] bg-white/[0.025] p-4">
                      <div className="mb-3 text-micro font-black uppercase tracking-[0.14em] text-slate-500">Then</div>
                      <select
                        value={builderAction}
                        onChange={(e) => setBuilderAction(e.target.value)}
                        className="mb-3 w-full rounded-ctl border border-white/10 bg-slate-950 px-3 py-2 text-xs text-white outline-none focus:border-cyan-400"
                      >
                        {AUTOMATION_ACTIONS.map((a) => (
                          <option key={a.id} value={a.id}>{a.label}</option>
                        ))}
                      </select>
                      {builderAction === 'create_task' && (
                        <div className="space-y-2">
                          <input
                            value={builderTaskTitle}
                            onChange={(e) => setBuilderTaskTitle(e.target.value)}
                            placeholder="Task title (use {company} as placeholder)"
                            className="w-full rounded-ctl border border-white/10 bg-slate-950 px-3 py-2 text-xs text-white outline-none focus:border-cyan-400"
                          />
                          <select
                            value={builderTaskPriority}
                            onChange={(e) => setBuilderTaskPriority(e.target.value)}
                            className="rounded-ctl border border-white/10 bg-slate-950 px-3 py-2 text-xs text-white outline-none focus:border-cyan-400"
                          >
                            {PRIORITY_OPTIONS.map((p) => <option key={p}>{p}</option>)}
                          </select>
                        </div>
                      )}
                      {builderAction === 'enroll_cadence' && (
                        <select
                          value={builderCadenceId}
                          onChange={(e) => setBuilderCadenceId(e.target.value)}
                          className="w-full rounded-ctl border border-white/10 bg-slate-950 px-3 py-2 text-xs text-white outline-none focus:border-cyan-400"
                        >
                          {BUILTIN_CADENCES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      )}
                    </div>

                    <div className="flex gap-3">
                      <button
                        type="button"
                        disabled={!builderName.trim() || builderSaving}
                        onClick={handleSave}
                        className="flex-1 rounded-full bg-cyan-400 py-2.5 text-meta font-black text-slate-950 transition hover:bg-cyan-300 disabled:opacity-40"
                      >
                        {builderSaving ? 'Saving…' : 'Save rule'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowBuilder(false)}
                        className="rounded-full border border-white/10 bg-white/5 px-6 py-2.5 text-meta font-black text-slate-400 hover:text-white transition"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {rules.length === 0 && !showBuilder ? (
                <div className="rounded-card border border-dashed border-white/10 px-5 py-12 text-center">
                  <Zap size={28} className="mx-auto mb-3 text-slate-700" />
                  <div className="text-sm font-bold text-slate-500">No automation rules yet.</div>
                  <div className="mt-1 text-xs text-slate-600">Rules fire automatically and appear in your Inbox for one-click execution.</div>
                  <button
                    type="button"
                    onClick={() => setShowBuilder(true)}
                    className="mt-4 inline-flex items-center gap-2 rounded-ctl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2.5 text-meta font-black uppercase tracking-[0.14em] text-cyan-300 hover:bg-cyan-500/20 transition"
                  >
                    <Plus size={12} /> Create first rule
                  </button>
                </div>
              ) : rules.map((rule) => {
                const tDef = AUTOMATION_TRIGGERS.find((t) => t.id === rule.trigger);
                const aDef = AUTOMATION_ACTIONS.find((a) => a.id === rule.action);
                return (
                  <div key={rule.id} className={`rounded-card border p-5 transition ${rule.enabled ? 'border-white/10 bg-slate-900/50' : 'border-white/[0.05] bg-slate-900/20 opacity-60'}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-start gap-3">
                        <button
                          type="button"
                          onClick={() => onToggleRule?.(rule.id, !rule.enabled)}
                          className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition ${rule.enabled ? 'bg-cyan-500 border-cyan-400' : 'border-white/20 bg-white/5'}`}
                        >
                          {rule.enabled && <CheckCircle2 size={9} className="text-slate-950" />}
                        </button>
                        <div>
                          <div className="text-sm font-bold text-white">{rule.name}</div>
                          <div className="mt-1 text-micro text-slate-500">
                            <span className="text-amber-400">When:</span> {tDef?.label}{rule.triggerValue ? ` · ${rule.triggerValue}${tDef?.valueLabel ? ' ' + tDef.valueLabel : ''}` : ''} &nbsp;
                            {tDef?.group && <span className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-micro text-slate-600">{tDef.group}</span>}
                            {' '}<span className="text-cyan-400">Then:</span> {aDef?.label}
                            {rule.action === 'create_task' && rule.actionParams?.title ? ` — "${rule.actionParams.title}"` : ''}
                            {rule.action === 'enroll_cadence' && rule.actionParams?.cadenceId ? ` — ${BUILTIN_CADENCES.find(c => c.id === rule.actionParams.cadenceId)?.name || rule.actionParams.cadenceId}` : ''}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onDeleteRule?.(rule.id)}
                        className="shrink-0 rounded-full p-1.5 text-slate-600 hover:text-rose-400 transition"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── CADENCES ── */}
          {tab === 'cadences' && (
            <div className="grid gap-5 md:grid-cols-2">
              {BUILTIN_CADENCES.map((cad) => {
                const enrolled = cadenceEnrollments.filter((e) => e.cadenceId === cad.id && e.status === 'active').length;
                return (
                  <div key={cad.id} className="rounded-card border border-white/[0.08] bg-slate-900/50 p-5">
                    <div className="mb-1 text-sm font-bold text-white">{cad.name}</div>
                    <div className="mb-3 text-micro text-slate-500">{cad.description}</div>
                    <div className="mb-4 space-y-1.5">
                      {cad.steps.map((step, i) => {
                        const Icon = CHANNEL_ICON[step.channel] || Zap;
                        return (
                          <div key={i} className="flex items-center gap-2.5 text-xs text-slate-300">
                            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-violet-400/25 bg-violet-500/10">
                              <Icon size={9} className="text-violet-400" />
                            </div>
                            <span className="text-slate-600 w-8 shrink-0">d{step.day}</span>
                            <span>{step.label}</span>
                          </div>
                        );
                      })}
                    </div>
                    {enrolled > 0 && (
                      <div className="text-micro text-violet-400 font-bold">{enrolled} active enrollment{enrolled !== 1 ? 's' : ''}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── ENROLLED ── */}
          {tab === 'enrolled' && (
            <div className="max-w-2xl space-y-4">
              {cadenceEnrollments.length === 0 ? (
                <div className="rounded-card border border-dashed border-white/10 px-5 py-12 text-center">
                  <CheckCircle2 size={28} className="mx-auto mb-3 text-slate-700" />
                  <div className="text-sm font-bold text-slate-500">No contacts enrolled in cadences yet.</div>
                  <div className="mt-1 text-xs text-slate-600">Open a contact's panel and enroll them in a cadence to track follow-up steps.</div>
                </div>
              ) : cadenceEnrollments.map((enrol) => {
                const cad = BUILTIN_CADENCES.find((c) => c.id === enrol.cadenceId);
                const stepsDone = enrol.currentStep || 0;
                const totalSteps = cad?.steps.length || 0;
                const pct = totalSteps > 0 ? Math.round((stepsDone / totalSteps) * 100) : 0;
                return (
                  <div key={enrol.id} className={`rounded-card border p-5 ${enrol.status === 'paused' ? 'border-amber-400/20 bg-amber-500/5' : enrol.status === 'completed' ? 'border-emerald-400/20 bg-emerald-500/5' : 'border-white/[0.08] bg-slate-900/50'}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <div className="text-sm font-bold text-white">{enrol.contactName || enrol.contactId}</div>
                        {enrol.companyName && <div className="text-xs text-slate-400">{enrol.companyName}</div>}
                        <div className="mt-1 text-micro text-slate-500">{enrol.cadenceName} · {stepsDone}/{totalSteps} steps</div>
                      </div>
                      <span className={`shrink-0 rounded-full border px-2.5 py-1 text-micro font-black uppercase tracking-[0.1em] ${
                        enrol.status === 'paused' ? 'border-amber-400/25 bg-amber-500/10 text-amber-300' :
                        enrol.status === 'completed' ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-300' :
                        'border-cyan-400/25 bg-cyan-500/10 text-cyan-300'
                      }`}>
                        {enrol.status || 'active'}
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div className="h-1.5 w-full rounded-full bg-white/5 overflow-hidden mb-2">
                      <div className="h-full rounded-full bg-violet-400" style={{ width: `${pct}%` }} />
                    </div>
                    {cad && <CadenceStepDots steps={cad.steps} currentStep={stepsDone} />}
                    {enrol.nextStepDueIso && enrol.status === 'active' && (
                      <div className="mt-2 text-micro text-slate-500">Next step due: {new Date(enrol.nextStepDueIso).toLocaleDateString()}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Analytics tab ── */}
          {tab === 'analytics' && (() => {
            const totalSent = emailTracking.length;
            const totalOpened = emailTracking.filter(t => t.opened).length;
            const totalClicked = emailTracking.filter(t => t.clicks?.length > 0).length;
            const totalReplied = emailTracking.filter(t => t.replied).length;
            const openRate = totalSent ? Math.round((totalOpened / totalSent) * 100) : 0;
            const clickRate = totalSent ? Math.round((totalClicked / totalSent) * 100) : 0;
            const replyRate = totalSent ? Math.round((totalReplied / totalSent) * 100) : 0;

            // Per-cadence breakdown
            const byEnrollment = {};
            for (const t of emailTracking) {
              const key = t.cadenceName || '—';
              if (!byEnrollment[key]) byEnrollment[key] = { sent: 0, opened: 0, clicked: 0, replied: 0 };
              byEnrollment[key].sent++;
              if (t.opened) byEnrollment[key].opened++;
              if (t.clicks?.length > 0) byEnrollment[key].clicked++;
              if (t.replied) byEnrollment[key].replied++;
            }

            return (
              <div className="p-6 space-y-6">
                {!smtpConfigured && (
                  <div className="flex items-center justify-between gap-4 rounded-card border border-amber-400/20 bg-amber-500/5 px-5 py-3">
                    <span className="text-xs text-amber-300">SMTP not configured — email sending and tracking are disabled.</span>
                    <button type="button" onClick={onOpenSmtpSettings} className="rounded-full bg-amber-400 px-4 py-1.5 text-micro font-black text-slate-950 hover:bg-amber-300 transition">Configure</button>
                  </div>
                )}

                {/* Summary stats */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[
                    { label: 'Sent', value: totalSent, color: 'text-cyan-400' },
                    { label: 'Opened', value: totalOpened, color: 'text-emerald-400' },
                    { label: 'Clicked', value: totalClicked, color: 'text-violet-400' },
                    { label: 'Replied', value: totalReplied, color: 'text-amber-400' },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="rounded-card border border-white/10 bg-slate-900 p-4 text-center">
                      <div className={`text-3xl font-black ${color}`}>{value}</div>
                      <div className="mt-1 text-micro font-black uppercase tracking-[0.14em] text-slate-500">{label}</div>
                    </div>
                  ))}
                </div>

                {/* Rate bars */}
                <div className="rounded-card border border-white/10 bg-slate-900 p-5 space-y-4">
                  <div className="text-micro font-black uppercase tracking-[0.2em] text-slate-500">Engagement rates</div>
                  {[
                    { label: 'Open rate', pct: openRate, color: 'bg-emerald-500' },
                    { label: 'Click rate', pct: clickRate, color: 'bg-violet-500' },
                    { label: 'Reply rate', pct: replyRate, color: 'bg-amber-500' },
                  ].map(({ label, pct, color }) => (
                    <div key={label}>
                      <div className="mb-1 flex justify-between text-xs">
                        <span className="text-slate-300">{label}</span>
                        <span className="font-bold text-white">{pct}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                        <div className={`h-full rounded-full ${color} transition-all duration-700`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Per-cadence table */}
                {Object.keys(byEnrollment).length > 0 && (
                  <div className="rounded-card border border-white/10 bg-slate-900 overflow-hidden">
                    <div className="px-5 py-3 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Per-cadence breakdown</div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-t border-white/5 text-micro font-black uppercase tracking-[0.14em] text-slate-600">
                            <th className="px-5 py-2 text-left">Cadence</th>
                            <th className="px-3 py-2 text-center">Sent</th>
                            <th className="px-3 py-2 text-center">Open%</th>
                            <th className="px-3 py-2 text-center">Click%</th>
                            <th className="px-3 py-2 text-center">Reply%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(byEnrollment).map(([name, s]) => (
                            <tr key={name} className="border-t border-white/5 hover:bg-white/[0.03]">
                              <td className="px-5 py-3 text-slate-200 font-semibold text-xs">{name}</td>
                              <td className="px-3 py-3 text-center text-slate-400 text-xs">{s.sent}</td>
                              <td className="px-3 py-3 text-center text-emerald-400 text-xs">{s.sent ? Math.round(s.opened/s.sent*100) : 0}%</td>
                              <td className="px-3 py-3 text-center text-violet-400 text-xs">{s.sent ? Math.round(s.clicked/s.sent*100) : 0}%</td>
                              <td className="px-3 py-3 text-center text-amber-400 text-xs">{s.sent ? Math.round(s.replied/s.sent*100) : 0}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Recent sends */}
                {emailTracking.length > 0 && (
                  <div className="rounded-card border border-white/10 bg-slate-900 overflow-hidden">
                    <div className="px-5 py-3 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Recent sends (last 20)</div>
                    <div className="divide-y divide-white/[0.05]">
                      {emailTracking.slice(0, 20).map((t) => (
                        <div key={t.id} className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-white/[0.02]">
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-xs font-semibold text-slate-200">{t.contactName || t.to || '—'}</div>
                            <div className="truncate text-micro text-slate-500">{t.subject || '(no subject)'}</div>
                          </div>
                          <div className="text-micro text-slate-600 shrink-0">{t.sentAtIso ? new Date(t.sentAtIso).toLocaleDateString() : '—'}</div>
                          <div className="flex shrink-0 gap-1">
                            <span className="rounded-full bg-cyan-500/15 px-2 py-0.5 text-micro font-bold text-cyan-300">Sent</span>
                            {t.opened && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-micro font-bold text-emerald-300">Opened</span>}
                            {t.clicks?.length > 0 && <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-micro font-bold text-violet-300">Clicked</span>}
                            {t.replied && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-micro font-bold text-amber-300">Replied</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {emailTracking.length === 0 && (
                  <div className="rounded-card border border-dashed border-white/10 py-16 text-center">
                    <Mail size={32} className="mx-auto mb-3 text-slate-700" />
                    <div className="text-sm font-bold text-slate-500">No email data yet</div>
                    <div className="mt-1 text-xs text-slate-600">Once you start sending cadence emails, analytics will appear here.</div>
                  </div>
                )}
              </div>
            );
          })()}

        </div>
      </div>
    </div>
  );
}
